import { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard,
  ListTodo,
  FolderOpen,
  BookOpen,
  Building2,
  Mic2,
  CreditCard,
  FileText,
  Settings,
  Bell,
  LogOut,
  ChevronLeft,
  Shield,
} from "lucide-react";

import authService from "@/services/authService";
import adminService from "@/services/adminService";

const NAV_ITEMS = [
  { path: "/admin", label: "Mission Control", icon: LayoutDashboard, exact: true },
  { path: "/admin/queues", label: "Work Queues", icon: ListTodo },
  { path: "/admin/workspaces", label: "User Workspaces", icon: FolderOpen },
  { path: "/admin/books", label: "Books", icon: BookOpen },
  { path: "/admin/publishers", label: "Publishers", icon: Building2 },
  { path: "/admin/narrators", label: "Narrators", icon: Mic2 },
  { path: "/admin/payments", label: "Payments", icon: CreditCard },
  { path: "/admin/audit", label: "Audit Log", icon: FileText },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [unreadEvents, setUnreadEvents] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    loadUnreadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminAccess = async () => {
    try {
      if (!authService.isAuthenticated()) {
        toast.error("Please login to access admin");
        navigate("/login");
        return;
      }

      const userData = await authService.getMe();
      if (userData.role !== "admin") {
        toast.error("Restricted Access: Admin only");
        navigate("/dashboard");
        return;
      }

      setUser(userData);
    } catch (error) {
      console.error("Admin access check failed:", error);
      toast.error("Access denied");
      navigate("/dashboard");
    }
  };

  const loadUnreadEvents = async () => {
    try {
      const events = await adminService.getEvents({ is_read: false });
      setUnreadEvents(Array.isArray(events) ? events.length : 0);
    } catch (error) {
      console.error("Error loading events:", error);
    }
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate("/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-slate-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarCollapsed ? "w-20" : "w-64"
        } bg-slate-800 border-r border-slate-700 flex flex-col transition-all duration-300`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="font-bold text-white text-sm">Mission Control</h1>
                <p className="text-xs text-slate-400">Admin Panel</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  active
                    ? "bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-blue-500/20 text-white border border-purple-500/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              >
                <Icon className={`w-5 h-5 ${active ? "text-purple-400" : ""}`} />
                {!sidebarCollapsed && <span className="text-sm">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-3 border-t border-slate-700">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700/50 transition-all"
          >
            <ChevronLeft
              className={`w-5 h-5 transition-transform ${
                sidebarCollapsed ? "rotate-180" : ""
              }`}
            />
            {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-slate-800/50 backdrop-blur-md border-b border-slate-700 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-white bg-slate-700/60 hover:bg-slate-600 border border-slate-500"

              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to App
              </Button>
            </div>

            <div className="flex items-center gap-4">
              {/* Notifications */}
              <button
                onClick={() => navigate("/admin/queues")}
                className="relative p-2 text-slate-400 hover:text-white transition-colors"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadEvents > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {unreadEvents > 9 ? "9+" : unreadEvents}
                  </span>
                )}
              </button>

              {/* User */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm text-white">{user.name}</p>
                  <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                    Admin
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-red-400"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
